/* ============================================================================
 * Wicked Clothes — cart "You May Also Like" ENDLESS SCROLL (load-more).
 * ----------------------------------------------------------------------------
 * Page 0 (~14 cards) renders inline in the drawer (snippets/wc-cart-recs.liquid,
 * instant). This appends pages 1+ from sections/cart-recs-more.liquid (paginated
 * bestsellers, cart items excluded) as the shopper scrolls near the bottom, so
 * the highest-intent surface never dead-ends at 14.
 *
 * Trigger = a throttled SCROLL listener on the drawer's scroll container
 * (<cart-drawer-items>, overflow-y:auto). We deliberately do NOT use
 * IntersectionObserver here: the recs are wired up while the drawer is CLOSED
 * (zero-size scroll root), which makes an IO unreliable — it wouldn't fire on a
 * fast fling. Reading scrollTop directly on each scroll event is immune to
 * scroll speed (SHEIN's distance-based approach).
 *
 * Dedup is client-side against handles already shown, so overlap with page 0 is
 * harmless. A "fill" step keeps loading if a dup-heavy batch didn't push the
 * bottom past the threshold. Re-inits on every drawer re-render via a
 * MutationObserver (same resilience pattern as wc-quick-add.js); appended cards'
 * quick-add auto-wires through wc-quick-add.js's own observer.
 * ========================================================================== */
(function () {
  'use strict';

  var SECTION = 'cart-recs-more';
  var MAX_FETCHES = 30;  // total fetch backstop across all size phases (re-fetches per phase)
  var NEAR_PX = 360;     // load when within this many px of the scroll bottom

  function throttle(fn, ms) {
    var last = 0, timer;
    return function () {
      var now = Date.now(), wait = ms - (now - last);
      if (wait <= 0) { last = now; fn(); }
      else { clearTimeout(timer); timer = setTimeout(function () { last = Date.now(); fn(); }, wait); }
    };
  }

  function initOne(recs) {
    if (!recs || recs.dataset.wcMoreInit) return;
    var grid = recs.querySelector('.wc-cart-recs__grid');
    if (!grid) return;
    recs.dataset.wcMoreInit = '1';

    // Find the scroll container — cart-drawer-items is overflow-y:auto.
    var scroller = recs.closest('cart-drawer-items') || recs.closest('.drawer__inner');
    if (!scroller) {
      var el = recs.parentElement;
      while (el && el !== document.body) {
        var oy = getComputedStyle(el).overflowY;
        if (oy === 'auto' || oy === 'scroll') { scroller = el; break; }
        el = el.parentElement;
      }
    }
    if (!scroller) return;

    // Seed the shown-set from the inline page-0 cards.
    var shown = {};
    grid.querySelectorAll('[data-wc-rec-handle]').forEach(function (li) {
      var h = li.getAttribute('data-wc-rec-handle');
      if (h) shown[h] = true;
    });

    var page = 1, loading = false, done = false, fetches = 0, phaseIdx = 0;

    function canScroll() { return scroller.clientHeight > 0 && scroller.scrollHeight > scroller.clientHeight + 1; }
    function nearBottom() { return (scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight) < NEAR_PX; }
    function finish() { done = true; scroller.removeEventListener('scroll', onScroll); }

    // Size phases (read fresh each call): the cart's PREFERRED (dominant) size
    // first, then every other cart size in turn — the same signal the quick-add
    // button uses, exposed by wc-quick-add.js. Empty => no size filtering.
    function phaseSizes() {
      var pref = window.wcCartSize || null;
      var all = window.wcCartSizes || [];
      var out = [];
      if (pref) out.push(pref);
      for (var i = 0; i < all.length; i++) { if (all[i] !== pref) out.push(all[i]); }
      return out;
    }

    function load() {
      if (loading || done) return;
      loading = true;
      var phases = phaseSizes();
      var phaseSize = phases.length ? phases[Math.min(phaseIdx, phases.length - 1)] : null;
      fetch('/?section_id=' + SECTION + '&page=' + page, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          loading = false;
          fetches++;
          if (!html) { finish(); return; }
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var hn = doc.querySelector('[data-wc-recs-hasnext]');
          var hasNext = hn && hn.getAttribute('data-wc-recs-hasnext') === '1';
          var lis = doc.querySelectorAll('li.grid__item[data-wc-rec-handle]');
          lis.forEach(function (li) {
            var h = li.getAttribute('data-wc-rec-handle');
            if (!h || shown[h]) return;                 // dedup — also gives "exclude already-shown" free
            if (phaseSize) {
              var card = li.querySelector('[data-wc-sizes]');
              var raw = card ? (card.getAttribute('data-wc-sizes') || '').trim() : '';
              if (raw) {
                // apparel: only the current phase's size. Other sizes are skipped
                // WITHOUT marking shown, so a later phase can pick them up.
                if ((' ' + raw + ' ').indexOf(' ' + phaseSize + ' ') === -1) return;
              } else if (phaseIdx > 0) {
                return;                                  // non-apparel only rides along in phase 0
              }
            }
            shown[h] = true;
            grid.appendChild(document.importNode(li, true));
          });
          page++;
          if (fetches > MAX_FETCHES) { finish(); return; }
          if (!hasNext) {
            // This size's pool is exhausted — advance to the next cart size and
            // rescan from page 1. Stop once every size has been drawn.
            if (phaseSize && phaseIdx + 1 < phases.length) {
              phaseIdx++; page = 1;
            } else { finish(); return; }
          }
          // Fill: keep loading while near the bottom (bounded by the guards above).
          if (canScroll() && nearBottom()) load();
        })
        .catch(function () { loading = false; });
    }

    function maybeLoad() {
      if (loading || done) return;
      if (!canScroll() || !nearBottom()) return;   // canScroll() bails while the drawer is closed
      load();
    }

    var onScroll = throttle(maybeLoad, 120);
    scroller.addEventListener('scroll', onScroll, { passive: true });
  }

  function scan() { document.querySelectorAll('.wc-cart-recs').forEach(initOne); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();

  // Drawer re-renders (add / qty / remove) swap in a fresh recs block; re-scan to
  // bind a new scroll listener. Guarded so our own appends don't loop the scan.
  function watchDrawer() {
    var drawer = document.querySelector('cart-drawer') || document.querySelector('.cart-drawer');
    if (!drawer) { setTimeout(watchDrawer, 500); return; }
    new MutationObserver(function () {
      if (drawer.querySelector('.wc-cart-recs:not([data-wc-more-init])')) scan();
    }).observe(drawer, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchDrawer);
  else watchDrawer();
})();
